import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("fi_user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState(() => localStorage.getItem("fi_token"));
  const [loading, setLoading] = useState(true);

  const persist = useCallback((t, u) => {
    if (t) localStorage.setItem("fi_token", t);
    else localStorage.removeItem("fi_token");
    if (u) localStorage.setItem("fi_user", JSON.stringify(u));
    else localStorage.removeItem("fi_user");
    setToken(t);
    setUser(u);
  }, []);

  const refresh = useCallback(async () => {
    if (!localStorage.getItem("fi_token")) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      persist(localStorage.getItem("fi_token"), data);
    } catch (e) {
      persist(null, null);
    } finally {
      setLoading(false);
    }
  }, [persist]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    persist(data.access_token, data.user);
    return data.user;
  };

  const signup = async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    persist(data.access_token, data.user);
    return data.user;
  };

  const logout = () => persist(null, null);

  const isAdmin = !!user?.roles?.includes("admin");

  return (
    <AuthContext.Provider value={{ user, token, loading, login, signup, logout, isAdmin, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
